import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Calendar,
  Clock,
  MapPin,
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  Ticket,
  DollarSign,
  UserCheck
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { goBack } from '../../utils/navigation';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Función para formatear fechas
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('T')[0].split('-');
  const date = new Date(parts[0], parts[1] - 1, parts[2]);
  return date.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
};

export default function EventoInscripcionPage() {
  const { eventoId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, trabajador } = useAuth();
  
  const [evento, setEvento] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [resultadoInscripcion, setResultadoInscripcion] = useState(null);
  
  const [respuestas, setRespuestas] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [datosPrecompletados, setDatosPrecompletados] = useState(false);
  const [cantidadEntradas, setCantidadEntradas] = useState(1);
  
  // Estado para lista de espera
  const [mostrarOpcionListaEspera, setMostrarOpcionListaEspera] = useState(false);
  const [motivoListaEspera, setMotivoListaEspera] = useState('');
  const [inscribiendoListaEspera, setInscribiendoListaEspera] = useState(false);
  const [resultadoListaEspera, setResultadoListaEspera] = useState(null);

  useEffect(() => {
    fetchEvento();
  }, [eventoId]);

  // Prellenar respuestas con datos del usuario
  useEffect(() => {
    if (evento && isAuthenticated && trabajador && !datosPrecompletados) {
      const nuevasRespuestas = { ...respuestas };
      
      evento.campos_formulario?.forEach(campo => {
        if (campo.prellenar_con && !nuevasRespuestas[campo.id]) {
          const mapeo = {
            nombre: trabajador.nombre,
            apellido: trabajador.apellido,
            email: trabajador.email,
            telefono: trabajador.celular,
            matricula: trabajador.numero_matricula,
            dni: trabajador.documento,
            distrito: 'Lomas de Zamora'
          };
          if (mapeo[campo.prellenar_con]) {
            nuevasRespuestas[campo.id] = mapeo[campo.prellenar_con];
          }
        }
      });
      
      setRespuestas(nuevasRespuestas);
      setDatosPrecompletados(true);
    }
  }, [evento, isAuthenticated, trabajador, datosPrecompletados]);

  const fetchEvento = async () => {
    try {
      const res = await axios.get(`${API_URL}/eventos/publicos/${eventoId}`);
      setEvento(res.data);
    } catch (err) {
      setError('Evento no encontrado');
    } finally {
      setLoading(false);
    }
  };

  const handleRespuestaChange = (campoId, valor) => {
    setRespuestas(prev => ({ ...prev, [campoId]: valor }));
    if (formErrors[campoId]) {
      setFormErrors(prev => ({ ...prev, [campoId]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    evento.campos_formulario?.forEach(campo => {
      if (campo.required) {
        const valor = respuestas[campo.id];
        if (!valor || (typeof valor === 'string' && !valor.trim())) {
          errors[campo.id] = `"${campo.label}" es obligatorio`;
        }
      }
    });
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError(null);
    setMostrarOpcionListaEspera(false);

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const res = await axios.post(
        `${API_URL}/eventos/publicos/${eventoId}/inscribirse`,
        { 
          respuestas,
          cantidad_entradas: evento.multiple_entradas ? cantidadEntradas : 1
        },
        { headers }
      );
      
      setResultadoInscripcion(res.data);
      setSuccess(true);
    } catch (err) {
      // Verificar si debemos ofrecer lista de espera
      if (err.response?.status === 409 && err.response?.data?.detail?.lista_espera_habilitada) {
        const detalle = err.response.data.detail;
        setMotivoListaEspera(detalle.codigo === 'CUPO_AGOTADO' ? 'cupo_agotado' : 'inscripciones_cerradas');
        setMostrarOpcionListaEspera(true);
        setError(null);
      } else {
        const errorMsg = err.response?.data?.detail;
        setError(typeof errorMsg === 'object' ? errorMsg.mensaje : errorMsg || 'Error al procesar la inscripción');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleInscribirseListaEspera = async () => {
    if (!validateForm()) {
      return;
    }

    setInscribiendoListaEspera(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const res = await axios.post(
        `${API_URL}/eventos/publicos/${eventoId}/lista-espera`,
        { 
          respuestas,
          cantidad_entradas: evento.multiple_entradas ? cantidadEntradas : 1
        },
        { headers }
      );
      
      setResultadoListaEspera(res.data);
      setMostrarOpcionListaEspera(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al anotarse en lista de espera');
    } finally {
      setInscribiendoListaEspera(false);
    }
  };

  // Renderizar campo según tipo
  const renderCampo = (campo) => {
    const valor = respuestas[campo.id] || '';
    const hasError = formErrors[campo.id];

    switch (campo.tipo) {
      case 'text':
      case 'email':
      case 'phone':
        return (
          <Input
            type={campo.tipo === 'email' ? 'email' : campo.tipo === 'phone' ? 'tel' : 'text'}
            value={valor}
            onChange={(e) => handleRespuestaChange(campo.id, e.target.value)}
            placeholder={campo.placeholder}
            className={hasError ? 'border-red-500' : ''}
            data-testid={`campo-${campo.id}`}
          />
        );
      
      case 'number':
        return (
          <Input
            type="number"
            value={valor}
            onChange={(e) => handleRespuestaChange(campo.id, e.target.value)}
            placeholder={campo.placeholder}
            min={campo.min_value}
            max={campo.max_value}
            className={hasError ? 'border-red-500' : ''}
            data-testid={`campo-${campo.id}`}
          />
        );
      
      case 'textarea':
        return (
          <Textarea
            value={valor}
            onChange={(e) => handleRespuestaChange(campo.id, e.target.value)}
            placeholder={campo.placeholder}
            className={hasError ? 'border-red-500' : ''}
            rows={3}
            data-testid={`campo-${campo.id}`}
          />
        );
      
      case 'date':
        return (
          <Input
            type="date"
            value={valor}
            onChange={(e) => handleRespuestaChange(campo.id, e.target.value)}
            className={hasError ? 'border-red-500' : ''}
            data-testid={`campo-${campo.id}`}
          />
        );
      
      case 'select':
      case 'dropdown':
        return (
          <Select
            value={valor}
            onValueChange={(v) => handleRespuestaChange(campo.id, v)}
          >
            <SelectTrigger className={hasError ? 'border-red-500' : ''} data-testid={`campo-${campo.id}`}>
              <SelectValue placeholder={campo.placeholder || 'Seleccionar...'} />
            </SelectTrigger>
            <SelectContent>
              {campo.options?.map((opt, i) => (
                <SelectItem key={i} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      
      case 'multiselect':
        return (
          <div className="space-y-2" data-testid={`campo-${campo.id}`}>
            {campo.options?.map((opt, i) => (
              <label key={i} className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={(valor || []).includes(opt)}
                  onCheckedChange={(checked) => {
                    const current = valor || [];
                    if (checked) {
                      handleRespuestaChange(campo.id, [...current, opt]);
                    } else {
                      handleRespuestaChange(campo.id, current.filter(v => v !== opt));
                    }
                  }}
                />
                <span className="text-sm">{opt}</span>
              </label>
            ))}
          </div>
        );
      
      case 'checkbox':
        return (
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={valor === true}
              onCheckedChange={(checked) => handleRespuestaChange(campo.id, checked)}
              data-testid={`campo-${campo.id}`}
            />
            <span className="text-sm">{campo.placeholder || 'Sí'}</span>
          </label>
        );
      
      case 'file':
        return (
          <Input
            type="file"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                handleRespuestaChange(campo.id, file.name);
              }
            }}
            className={hasError ? 'border-red-500' : ''}
            data-testid={`campo-${campo.id}`}
          />
        );
      
      default:
        return (
          <Input
            value={valor}
            onChange={(e) => handleRespuestaChange(campo.id, e.target.value)}
            placeholder={campo.placeholder}
            className={hasError ? 'border-red-500' : ''}
            data-testid={`campo-${campo.id}`}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  if (!evento) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <Calendar className="w-16 h-16 text-gray-300 mb-4" />
        <p className="text-gray-500 mb-4">Evento no encontrado</p>
        <button onClick={() => goBack()} className="text-violet-600 hover:underline">
          ← Volver
        </button>
      </div>
    );
  }

  // Pantalla de éxito
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white shadow-sm">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo-colegio.png" alt="Logo" className="h-12 w-auto" />
            </Link>
          </div>
        </header>
        
        <main className="max-w-lg mx-auto px-4 py-12">
          <Card>
            <CardContent className="pt-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Inscripción exitosa!</h2>
              <p className="text-gray-600 mb-4">
                Te inscribiste correctamente a <strong>{evento.nombre}</strong>.
              </p>
              
              {/* Info de deuda si aplica */}
              {resultadoInscripcion?.tiene_deuda && resultadoInscripcion?.deuda_info && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4 text-left">
                  <p className="font-medium text-orange-800 text-sm">
                    Se detectó una deuda pendiente de ${resultadoInscripcion.deuda_info.total?.toLocaleString()}. 
                    El monto de tu inscripción fue ajustado según las reglas del evento.
                  </p>
                </div>
              )}
              
              {resultadoInscripcion?.payment_status === 'pendiente' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 text-left">
                  <p className="font-medium text-amber-800 flex items-center gap-2">
                    <DollarSign className="w-5 h-5" />
                    Pago pendiente: ${resultadoInscripcion.monto_a_pagar?.toLocaleString()}
                  </p>
                  {evento.configuracion_pago?.datos_transferencia && (
                    <div className="mt-2 text-sm text-amber-700">
                      <p className="font-medium">Datos para transferencia:</p>
                      <pre className="whitespace-pre-wrap mt-1">{evento.configuracion_pago.datos_transferencia}</pre>
                    </div>
                  )}
                </div>
              )}
              
              {resultadoInscripcion?.ticket_id && (
                <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 mb-4">
                  <p className="font-medium text-violet-800 flex items-center justify-center gap-2">
                    <Ticket className="w-5 h-5" />
                    Tu código de entrada: <strong>{resultadoInscripcion.ticket_id}</strong>
                  </p>
                  <p className="text-sm text-violet-600 mt-1 mb-3">Recibirás tu ticket por email</p>
                  <Link to={`/eventos/ticket/${resultadoInscripcion.ticket_id}`}>
                    <Button variant="outline" className="w-full border-violet-300 text-violet-700 hover:bg-violet-100" data-testid="btn-ver-ticket">
                      <Ticket className="w-4 h-4 mr-2" />
                      Ver mi entrada
                    </Button>
                  </Link>
                </div>
              )}
              
              <div className="space-y-3 mt-6">
                {resultadoInscripcion?.ticket_id && (
                  <Link to={`/eventos/ticket/${resultadoInscripcion.ticket_id}`}>
                    <Button className="w-full bg-violet-600 hover:bg-violet-700" data-testid="btn-ver-ticket-main">
                      <Ticket className="w-4 h-4 mr-2" />
                      Ver mi entrada con QR
                    </Button>
                  </Link>
                )}
                <Link to="/eventos">
                  <Button variant={resultadoInscripcion?.ticket_id ? "outline" : "default"} className={resultadoInscripcion?.ticket_id ? "w-full" : "w-full bg-violet-600 hover:bg-violet-700"} data-testid="btn-ver-mas-eventos">
                    Ver más eventos
                  </Button>
                </Link>
                <Button variant="outline" className="w-full" onClick={() => goBack()} data-testid="btn-volver-inicio">
                  Volver
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // Pantalla de éxito de lista de espera
  if (resultadoListaEspera) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white shadow-sm">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo-colegio.png" alt="Logo" className="h-12 w-auto" />
            </Link>
          </div>
        </header>
        
        <main className="max-w-lg mx-auto px-4 py-12">
          <Card>
            <CardContent className="pt-8 text-center">
              <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Clock className="w-10 h-10 text-orange-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Te anotaste en la lista de espera!</h2>
              <p className="text-gray-600 mb-4">
                Quedaste en la lista de espera para <strong>{evento.nombre}</strong>.
              </p>
              
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                <p className="text-lg font-bold text-orange-800">
                  Tu posición: #{resultadoListaEspera.posicion}
                </p>
                <p className="text-sm text-orange-600 mt-1">
                  Te notificaremos a <strong>{resultadoListaEspera.email}</strong> cuando haya lugar disponible.
                </p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left text-sm text-gray-600">
                <p className="font-medium text-gray-700 mb-2">¿Cómo funciona?</p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>Si alguien cancela su inscripción, se liberará un lugar</li>
                  <li>Te contactaremos para que confirmes tu inscripción</li>
                  <li>Tus datos ya quedaron guardados</li>
                </ul>
              </div>
              
              <div className="space-y-3">
                <Link to="/eventos">
                  <Button className="w-full bg-violet-600 hover:bg-violet-700" data-testid="btn-ver-mas-eventos">
                    Ver más eventos
                  </Button>
                </Link>
                <Button variant="outline" className="w-full" onClick={() => goBack()} data-testid="btn-volver">
                  Volver
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo-colegio.png" alt="Logo" className="h-12 w-auto" />
            </Link>
            <button onClick={() => goBack()} className="text-gray-600 hover:text-gray-900 flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Volver
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-8">
        {/* Info compacta del evento */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-2 mb-3">
              {evento.modalidad_admision === 'ticket_qr' && (
                <Badge className="bg-violet-100 text-violet-700">
                  <Ticket className="w-3 h-3 mr-1" />
                  Con entrada
                </Badge>
              )}
              {evento.configuracion_pago?.tipo === 'gratis' && (
                <Badge className="bg-green-100 text-green-700">Gratis</Badge>
              )}
              {evento.configuracion_pago?.tipo === 'pago_fijo' && (
                <Badge className="bg-amber-100 text-amber-700">
                  <DollarSign className="w-3 h-3 mr-1" />
                  ${evento.configuracion_pago.precio?.toLocaleString()}
                </Badge>
              )}
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">{evento.nombre}</h1>
            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
              {evento.fecha_evento && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4 text-violet-600" />
                  {formatDate(evento.fecha_evento)}
                </span>
              )}
              {evento.hora_evento && (
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4 text-violet-600" />
                  {evento.hora_evento} hs
                </span>
              )}
              {evento.lugar && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4 text-violet-600" />
                  {evento.lugar}
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Formulario de inscripción */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-violet-600" />
              Formulario de Inscripción
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Mensaje de datos precompletados */}
            {datosPrecompletados && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                <UserCheck className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-green-700 text-sm font-medium">Datos precompletados</p>
                  <p className="text-green-600 text-sm">Completamos algunos campos automáticamente. Verificalos antes de enviar.</p>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Opción de Lista de Espera */}
            {mostrarOpcionListaEspera && (
              <div className="bg-orange-50 border border-orange-300 rounded-lg p-6 mb-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-orange-900 text-lg mb-1">
                      {motivoListaEspera === 'cupo_agotado' 
                        ? '¡Se agotaron los lugares!' 
                        : 'Las inscripciones están cerradas'}
                    </h3>
                    <p className="text-orange-700 mb-4">
                      {motivoListaEspera === 'cupo_agotado'
                        ? 'El cupo para este evento está completo, pero podés anotarte en la lista de espera.'
                        : 'Las inscripciones para este evento ya cerraron, pero podés anotarte en la lista de espera.'}
                    </p>
                    <div className="bg-white/70 rounded-lg p-3 mb-4 text-sm text-orange-800">
                      <p className="font-medium mb-1">¿Cómo funciona la lista de espera?</p>
                      <ul className="list-disc list-inside space-y-1 text-orange-700">
                        <li>Si alguien cancela, te avisamos por email</li>
                        <li>Tus datos ya quedaron guardados</li>
                        <li>Es por orden de llegada</li>
                      </ul>
                    </div>
                    <div className="flex gap-3">
                      <Button
                        onClick={handleInscribirseListaEspera}
                        disabled={inscribiendoListaEspera}
                        className="bg-orange-600 hover:bg-orange-700 text-white"
                        data-testid="btn-anotarse-lista-espera"
                      >
                        {inscribiendoListaEspera ? 'Anotando...' : 'Anotarme en lista de espera'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setMostrarOpcionListaEspera(false)}
                        className="border-orange-300 text-orange-700 hover:bg-orange-100"
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Agrupar campos por sección */}
              {(() => {
                const campos = evento.campos_formulario || [];
                const secciones = {};
                const sinSeccion = [];

                campos.forEach(campo => {
                  if (campo.seccion) {
                    if (!secciones[campo.seccion]) {
                      secciones[campo.seccion] = [];
                    }
                    secciones[campo.seccion].push(campo);
                  } else {
                    sinSeccion.push(campo);
                  }
                });

                return (
                  <>
                    {/* Campos sin sección */}
                    {sinSeccion.map(campo => (
                      <div key={campo.id}>
                        <Label htmlFor={campo.id}>
                          {campo.label} {campo.required && <span className="text-red-500">*</span>}
                        </Label>
                        {renderCampo(campo)}
                        {formErrors[campo.id] && (
                          <p className="text-red-500 text-xs mt-1">{formErrors[campo.id]}</p>
                        )}
                      </div>
                    ))}

                    {/* Campos agrupados por sección */}
                    {Object.entries(secciones).map(([seccion, camposSeccion]) => (
                      <div key={seccion} className="border-t pt-6">
                        <h3 className="font-medium text-gray-900 mb-4">{seccion}</h3>
                        <div className="space-y-4">
                          {camposSeccion.map(campo => (
                            <div key={campo.id}>
                              <Label htmlFor={campo.id}>
                                {campo.label} {campo.required && <span className="text-red-500">*</span>}
                              </Label>
                              {renderCampo(campo)}
                              {formErrors[campo.id] && (
                                <p className="text-red-500 text-xs mt-1">{formErrors[campo.id]}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </>
                );
              })()}

              {/* Selector de cantidad de entradas (solo si está habilitado) */}
              {evento.multiple_entradas && (
                <div className="border-t pt-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <Label htmlFor="cantidad_entradas" className="text-blue-800 font-medium flex items-center gap-2">
                      <Ticket className="w-4 h-4" />
                      ¿Cuántas entradas querés reservar?
                    </Label>
                    <p className="text-blue-600 text-xs mb-3">Máximo {evento.max_entradas_por_usuario || 1} entrada(s) por persona</p>
                    <div className="flex items-center gap-3">
                      <Select
                        value={String(cantidadEntradas)}
                        onValueChange={(v) => setCantidadEntradas(parseInt(v))}
                      >
                        <SelectTrigger className="w-32 bg-white" data-testid="cantidad-entradas-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: evento.max_entradas_por_usuario || 1 }, (_, i) => i + 1).map(num => (
                            <SelectItem key={num} value={String(num)}>
                              {num} {num === 1 ? 'entrada' : 'entradas'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {cantidadEntradas > 1 && evento.configuracion_pago?.tipo !== 'gratis' && (
                        <span className="text-sm text-blue-700 font-medium">
                          Total: ${((evento.configuracion_pago?.precio || 0) * cantidadEntradas).toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-violet-600 hover:bg-violet-700"
                disabled={submitting}
                data-testid="btn-confirmar-inscripcion"
              >
                {submitting ? 'Procesando...' : 'Confirmar Inscripción'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
